const DEFAULT_ACTIVE_POLL_INTERVAL_MS = 60000;
const DEFAULT_IDLE_POLL_INTERVAL_MS = 180000;
const DEFAULT_HIDDEN_POLL_INTERVAL_MS = 300000;
const DEFAULT_MAX_BACKOFF_MS = 300000;
const DEFAULT_BATCH_LIMIT = 100;
const DEFAULT_IDLE_AFTER_MS = 300000;
const DEFAULT_LEADER_STALE_MS = 30000;
const LEADER_STORAGE_KEY = 'shipsops.sync.leader.v1';

function defaultScheduler() {
  return {
    setTimeout: (callback, delay) => globalThis.setTimeout(callback, delay),
    clearTimeout: (id) => globalThis.clearTimeout(id),
  };
}

function defaultVisibility() {
  return globalThis.document || {
    hidden: false,
    addEventListener() {},
    removeEventListener() {},
  };
}

function defaultWindow() {
  return globalThis.window || {
    addEventListener() {},
    removeEventListener() {},
  };
}

function defaultChannelFactory(name) {
  if (!('BroadcastChannel' in globalThis)) return null;
  return new BroadcastChannel(name);
}

function defaultEventSourceFactory(url) {
  if (!('EventSource' in globalThis)) return null;
  return new EventSource(url, { withCredentials: true });
}

function defaultStorage() {
  try {
    const storage = globalThis.localStorage;
    if (!storage) return null;
    const probe = 'shipsops.sync.probe';
    storage.setItem(probe, '1');
    storage.removeItem(probe);
    return storage;
  } catch {
    return null;
  }
}

function createInstanceId() {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();
  return `tab-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

function nonNegativeInteger(value, fallback = 0) {
  const number = Number(value);
  if (!Number.isFinite(number)) return fallback;
  return Math.max(0, Math.trunc(number));
}

function nowMs() {
  return globalThis.performance?.now?.() ?? Date.now();
}

export class OutboxSyncClient {
  constructor({
    api = null,
    onEvents = async () => {},
    onError = () => {},
    onStatusChange = () => {},
    pollIntervalMs = DEFAULT_ACTIVE_POLL_INTERVAL_MS,
    idlePollIntervalMs = DEFAULT_IDLE_POLL_INTERVAL_MS,
    hiddenPollIntervalMs = DEFAULT_HIDDEN_POLL_INTERVAL_MS,
    maxBackoffMs = DEFAULT_MAX_BACKOFF_MS,
    idleAfterMs = DEFAULT_IDLE_AFTER_MS,
    leaderStaleMs = DEFAULT_LEADER_STALE_MS,
    batchLimit = DEFAULT_BATCH_LIMIT,
    scheduler = defaultScheduler(),
    visibility = defaultVisibility(),
    windowTarget = defaultWindow(),
    channelFactory = defaultChannelFactory,
    eventSourceFactory = defaultEventSourceFactory,
    streamEnabled = true,
    storage = defaultStorage(),
    instanceId = createInstanceId(),
  } = {}) {
    this.api = api;
    this.onEvents = onEvents;
    this.onError = onError;
    this.onStatusChange = onStatusChange;
    this.status = 'connecting';
    this.streamConfirmedOpen = false;
    this.pollIntervalMs = Math.max(3000, nonNegativeInteger(pollIntervalMs, DEFAULT_ACTIVE_POLL_INTERVAL_MS));
    this.idlePollIntervalMs = Math.max(this.pollIntervalMs, nonNegativeInteger(idlePollIntervalMs, DEFAULT_IDLE_POLL_INTERVAL_MS));
    this.hiddenPollIntervalMs = Math.max(this.idlePollIntervalMs, nonNegativeInteger(hiddenPollIntervalMs, DEFAULT_HIDDEN_POLL_INTERVAL_MS));
    this.maxBackoffMs = Math.max(this.idlePollIntervalMs, nonNegativeInteger(maxBackoffMs, DEFAULT_MAX_BACKOFF_MS));
    this.idleAfterMs = Math.max(30000, nonNegativeInteger(idleAfterMs, DEFAULT_IDLE_AFTER_MS));
    this.batchLimit = Math.max(1, Math.min(200, nonNegativeInteger(batchLimit, DEFAULT_BATCH_LIMIT)));
    this.leaderStaleMs = Math.max(15000, nonNegativeInteger(leaderStaleMs, DEFAULT_LEADER_STALE_MS));
    this.scheduler = scheduler;
    this.visibility = visibility;
    this.windowTarget = windowTarget;
    this.channelFactory = channelFactory;
    this.eventSourceFactory = eventSourceFactory;
    this.streamEnabled = Boolean(streamEnabled);
    this.storage = storage;
    this.instanceId = String(instanceId || createInstanceId());
    this.leaderKey = LEADER_STORAGE_KEY;
    this.leader = false;
    this.cursor = 0;
    this.running = false;
    this.initialized = false;
    this.failures = 0;
    this.timer = null;
    this.inFlight = null;
    this.channel = null;
    this.stream = null;
    this.lastActivityAt = nowMs();
    this.boundMessage = event => this.receive(event?.data);
    this.boundVisibility = () => this.visibilityChanged();
    this.boundActivity = () => this.activity();
    this.boundFocus = () => this.focused();
    this.boundStorage = event => this.storageChanged(event);
    this.boundStreamMessage = event => this.receiveStream(event);
    this.boundStreamError = event => this.streamFailed(event);
    this.boundStreamOpen = () => this.streamOpened();
    this.boundUnload = () => this.releaseLeadership();
  }

  /** 'live' (SSE confirmed open), 'reconnecting' (repeated failures/backoff),
   *  'fallback' (this tab should be streaming but isn't - disabled/not-yet-open),
   *  'follower' (another tab is leader; relies on BroadcastChannel, not a problem),
   *  'hidden' (tab not visible, deliberately slow). Only 'reconnecting'/'fallback'
   *  are worth surfacing to the user; the rest are expected/benign. */
  computeStatus() {
    if (this.visibility?.hidden) return 'hidden';
    if (!this.leader) return 'follower';
    if (this.streamConfirmedOpen) return 'live';
    if (this.failures > 0) return 'reconnecting';
    return 'fallback';
  }

  emitStatus() {
    const next = this.computeStatus();
    if (next === this.status) return;
    this.status = next;
    try { this.onStatusChange(next); } catch {}
  }

  streamOpened() {
    this.streamConfirmedOpen = true;
    this.emitStatus();
  }

  async start() {
    if (this.running) return;
    this.running = true;
    this.lastActivityAt = nowMs();
    this.channel = this.channelFactory?.('shipsops-domain-events') || null;
    this.channel?.addEventListener?.('message', this.boundMessage);
    this.visibility?.addEventListener?.('visibilitychange', this.boundVisibility);
    this.windowTarget?.addEventListener?.('focus', this.boundFocus);
    this.windowTarget?.addEventListener?.('pointerdown', this.boundActivity, { passive: true });
    this.windowTarget?.addEventListener?.('keydown', this.boundActivity, { passive: true });
    this.windowTarget?.addEventListener?.('storage', this.boundStorage);
    // pagehide (not beforeunload) so a closed/refreshed/crashed tab frees the
    // leader slot immediately instead of making other tabs wait leaderStaleMs.
    this.windowTarget?.addEventListener?.('pagehide', this.boundUnload);
    await this.initialize();
  }

  stop() {
    this.running = false;
    this.cancelTimer();
    this.visibility?.removeEventListener?.('visibilitychange', this.boundVisibility);
    this.windowTarget?.removeEventListener?.('focus', this.boundFocus);
    this.windowTarget?.removeEventListener?.('pointerdown', this.boundActivity);
    this.windowTarget?.removeEventListener?.('keydown', this.boundActivity);
    this.windowTarget?.removeEventListener?.('storage', this.boundStorage);
    this.windowTarget?.removeEventListener?.('pagehide', this.boundUnload);
    this.closeStream();
    this.releaseLeadership();
    if (this.channel) {
      this.channel.removeEventListener?.('message', this.boundMessage);
      this.channel.close?.();
      this.channel = null;
    }
  }

  async initialize() {
    if (!this.running || this.initialized) return;
    if (this.visibility?.hidden || !this.ensureLeadership()) {
      this.schedule(this.visibility?.hidden ? this.hiddenPollIntervalMs : Math.min(this.currentInterval(), Math.max(3000, Math.floor(this.leaderStaleMs / 2))));
      this.emitStatus();
      return;
    }
    if (!this.api?.call) {
      this.initialized = true;
      return;
    }
    try {
      const response = await this.api.call('sync.cursor.get', {}, { dedupeKey: 'sync.cursor.get' });
      if (!this.running) return;
      this.cursor = nonNegativeInteger(response?.cursor, 0);
      this.initialized = true;
      this.failures = 0;
      this.openStream();
      this.schedule(this.currentInterval());
      this.emitStatus();
    } catch (error) {
      this.failed(error);
    }
  }

  poll() {
    if (!this.running) return Promise.resolve();
    if (this.inFlight) return this.inFlight;
    if (this.visibility?.hidden) {
      this.schedule(this.hiddenPollIntervalMs);
      this.emitStatus();
      return Promise.resolve();
    }
    if (!this.ensureLeadership()) {
      this.closeStream();
      this.schedule(Math.min(this.currentInterval(), Math.max(3000, Math.floor(this.leaderStaleMs / 2))));
      this.emitStatus();
      return Promise.resolve();
    }
    this.openStream();
    if (!this.initialized) {
      this.inFlight = Promise.resolve(this.initialize()).finally(() => { this.inFlight = null; });
      return this.inFlight;
    }
    this.inFlight = this.performPoll().finally(() => { this.inFlight = null; });
    return this.inFlight;
  }

  async performPoll() {
    try {
      this.heartbeatLeadership();
      const response = await this.api.call('sync.batch.get', {
        cursor: this.cursor,
        limit: this.batchLimit,
      }, { dedupeKey: 'sync.batch.get' });
      if (!this.running) return;
      this.heartbeatLeadership();
      this.cursor = Math.max(this.cursor, nonNegativeInteger(response?.next_cursor, this.cursor));
      this.failures = 0;
      const events = Array.isArray(response?.events) ? response.events : [];
      if (events.length) {
        this.channel?.postMessage?.({ type: 'events', cursor: this.cursor, events });
        await this.deliver(events);
      }
      this.schedule(response?.has_more ? 0 : this.currentInterval());
    } catch (error) {
      this.failed(error);
    }
  }

  publishLocal(events) {
    const list = Array.isArray(events) ? events : [];
    if (!list.length) return;
    this.channel?.postMessage?.({ type: 'events', cursor: this.cursor, events: list });
    void this.deliver(list);
    this.schedule(0);
  }

  receive(message) {
    if (!this.running) return;
    const events = Array.isArray(message?.events) ? message.events : [];
    if (!events.length) return;
    this.cursor = Math.max(this.cursor, nonNegativeInteger(message?.cursor, this.cursor));
    void this.deliver(events);
  }

  async deliver(events) {
    try {
      await this.onEvents(events);
    } catch (error) {
      this.reportError(error);
    }
  }

  failed(error) {
    if (!this.running) return;
    this.failures += 1;
    this.reportError(error);
    const delay = Math.min(this.maxBackoffMs, this.currentInterval() * (2 ** this.failures));
    this.schedule(delay);
    this.emitStatus();
  }

  reportError(error) {
    try { this.onError(error); } catch {}
  }

  activity() {
    if (!this.running) return;
    this.lastActivityAt = nowMs();
  }

  focused() {
    if (!this.running) return;
    this.lastActivityAt = nowMs();
    this.openStream();
    this.schedule(Math.min(this.currentInterval(), 15000));
  }

  storageChanged(event) {
    if (!this.running || event?.key !== this.leaderKey) return;
    const leader = this.currentLeader();
    this.leader = leader?.id === this.instanceId;
    if (!this.leader) {
      this.closeStream();
      this.schedule(Math.min(this.currentInterval(), Math.max(3000, Math.floor(this.leaderStaleMs / 2))));
    } else {
      this.openStream();
    }
    this.emitStatus();
  }

  visibilityChanged() {
    if (!this.running) return;
    if (this.visibility?.hidden) {
      this.closeStream();
      this.schedule(this.hiddenPollIntervalMs);
      this.emitStatus();
      return;
    }
    this.openStream();
    this.schedule(Math.min(this.currentInterval(), 15000));
    this.emitStatus();
  }

  streamUrl() {
    const endpoint = String(this.api?.endpoint || 'api.php');
    const url = new URL(endpoint, globalThis.location?.href || 'http://localhost/');
    url.searchParams.set('action', 'sync.stream.get');
    url.searchParams.set('cursor', String(this.cursor));
    url.searchParams.set('limit', String(this.batchLimit));
    return url.toString();
  }

  openStream() {
    if (!this.running || !this.initialized || !this.streamEnabled || this.visibility?.hidden || !this.leader || !this.eventSourceFactory) return false;
    if (this.stream) return true;
    const source = this.eventSourceFactory(this.streamUrl());
    if (!source) return false;
    this.stream = source;
    source.addEventListener?.('domain-events', this.boundStreamMessage);
    source.addEventListener?.('message', this.boundStreamMessage);
    source.addEventListener?.('error', this.boundStreamError);
    source.addEventListener?.('open', this.boundStreamOpen);
    return true;
  }

  closeStream() {
    this.streamConfirmedOpen = false;
    if (!this.stream) return;
    this.stream.removeEventListener?.('domain-events', this.boundStreamMessage);
    this.stream.removeEventListener?.('message', this.boundStreamMessage);
    this.stream.removeEventListener?.('error', this.boundStreamError);
    this.stream.removeEventListener?.('open', this.boundStreamOpen);
    this.stream.close?.();
    this.stream = null;
  }

  receiveStream(event) {
    if (!this.running) return;
    let payload = null;
    try { payload = JSON.parse(String(event?.data || '{}')); } catch { return; }
    const events = Array.isArray(payload?.events) ? payload.events : [];
    this.cursor = Math.max(this.cursor, nonNegativeInteger(payload?.next_cursor ?? payload?.last_cursor ?? event?.lastEventId, this.cursor));
    this.failures = 0;
    // Receiving anything on this stream (even a plain heartbeat batch) proves it is
    // live; emit that before the immediate close+reopen below so the connection-cycling
    // implementation detail never shows up as a "reconnecting" blip to the user.
    this.streamConfirmedOpen = true;
    this.emitStatus();
    if (events.length) {
      this.channel?.postMessage?.({ type: 'events', cursor: this.cursor, events });
      void this.deliver(events);
    }
    this.closeStream();
    this.openStream();
    this.schedule(payload?.has_more ? 0 : this.currentInterval());
  }

  streamFailed(event) {
    if (!this.running) return;
    this.closeStream();
    this.schedule(Math.min(this.currentInterval(), 15000));
    this.emitStatus();
  }


  currentLeader() {
    if (!this.storage) return null;
    try {
      const decoded = JSON.parse(this.storage.getItem(this.leaderKey) || 'null');
      if (!decoded || typeof decoded !== 'object') return null;
      return { id: String(decoded.id || ''), heartbeatAt: Number(decoded.heartbeatAt || 0) };
    } catch {
      return null;
    }
  }

  ensureLeadership() {
    if (!this.storage) {
      this.leader = true;
      return true;
    }
    const now = Date.now();
    const leader = this.currentLeader();
    if (!leader?.id || leader.id === this.instanceId || now - leader.heartbeatAt > this.leaderStaleMs) {
      try {
        this.storage.setItem(this.leaderKey, JSON.stringify({ id: this.instanceId, heartbeatAt: now }));
        const confirmed = this.currentLeader();
        this.leader = confirmed?.id === this.instanceId;
        return this.leader;
      } catch {
        this.leader = true;
        return true;
      }
    }
    this.leader = false;
    return false;
  }

  heartbeatLeadership() {
    if (!this.leader || !this.storage) return;
    try {
      this.storage.setItem(this.leaderKey, JSON.stringify({ id: this.instanceId, heartbeatAt: Date.now() }));
    } catch {
      this.leader = true;
    }
  }

  releaseLeadership() {
    this.closeStream();
    if (!this.storage || !this.leader) return;
    try {
      const leader = this.currentLeader();
      if (leader?.id === this.instanceId) this.storage.removeItem(this.leaderKey);
    } catch {}
    this.leader = false;
  }

  currentInterval() {
    if (this.visibility?.hidden) return this.hiddenPollIntervalMs;
    const idleFor = nowMs() - this.lastActivityAt;
    return idleFor >= this.idleAfterMs ? this.idlePollIntervalMs : this.pollIntervalMs;
  }

  schedule(delay) {
    if (!this.running) return;
    this.cancelTimer();
    this.timer = this.scheduler.setTimeout(() => {
      this.timer = null;
      return this.poll();
    }, Math.max(0, nonNegativeInteger(delay, this.currentInterval())));
  }

  cancelTimer() {
    if (this.timer === null) return;
    this.scheduler.clearTimeout(this.timer);
    this.timer = null;
  }
}
